import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';

import { composerSafeButtonHandlers } from './composerSafeButtonHandlers';
import type {
  AiAssistantDocumentContext,
  AiAssistantPageContext,
} from './types';

import styles from './PageContextChips.module.scss';

type ContextChipItem = {
  key: string;
  label: string;
  title?: string;
};

type ContextChipsProps = {
  className?: string;
  items: ContextChipItem[];
  onActivate?: (key: string) => void;
  onRemove?: (key: string) => void;
  removeTitleKey:
    | 'app.aiAssistant.removePageContext'
    | 'app.aiAssistant.removeDocumentContext';
  testId: string;
  removeTestId: string;
  /** Light-on-primary pills for the user chat bubble. */
  tone?: 'default' | 'onPrimary';
};

type PageContextChipsProps = {
  className?: string;
  items: AiAssistantPageContext[];
  onNavigate?: (href: string) => void;
  onRemove?: (href: string) => void;
  /** Light-on-primary pills for the user chat bubble. */
  tone?: 'default' | 'onPrimary';
};

type DocumentContextChipsProps = {
  className?: string;
  items: AiAssistantDocumentContext[];
  onRemove?: (id: string) => void;
  tone?: 'default' | 'onPrimary';
};

function ChipLabel({ label }: { label: string }) {
  return (
    <TextWithTooltip
      textProps={{ lineClamp: 1, span: true }}
      tooltipProps={{ position: 'top' }}
    >
      {label}
    </TextWithTooltip>
  );
}

function ContextChips({
  className,
  items,
  onActivate,
  onRemove,
  removeTestId,
  removeTitleKey,
  testId,
  tone = 'default',
}: ContextChipsProps) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return null;
  }

  return (
    <ul
      className={clsx(
        styles['chips'],
        tone === 'onPrimary' && styles['chips--on-primary'],
        className
      )}
    >
      {items.map((item) => {
        const isNavigable = onActivate != null && onRemove == null;
        return (
          <li
            className={clsx(styles['chip'], {
              [styles['chip--navigable']]: isNavigable,
            })}
            data-testid={testId}
            key={item.key}
            title={item.title ?? item.key}
          >
            {isNavigable ? (
              <button
                className={styles['chip-action']}
                type="button"
                {...composerSafeButtonHandlers(() => onActivate(item.key))}
              >
                <ChipLabel label={item.label} />
              </button>
            ) : (
              <span className={styles['chip-label']}>
                <ChipLabel label={item.label} />
              </span>
            )}
            {onRemove ? (
              <button
                className={styles['chip-remove']}
                data-testid={removeTestId}
                title={t(removeTitleKey, {
                  label: item.label,
                })}
                type="button"
                {...composerSafeButtonHandlers(() => onRemove(item.key))}
              >
                <GoogleFontIcon name="close" />
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function PageContextChips({
  className,
  items,
  onNavigate,
  onRemove,
  tone = 'default',
}: PageContextChipsProps) {
  return (
    <ContextChips
      className={className}
      items={items.map((item) => ({
        key: item.href,
        label: item.label,
        title: item.href,
      }))}
      onActivate={onNavigate}
      onRemove={onRemove}
      removeTestId="ai-assistant-page-context-remove"
      removeTitleKey="app.aiAssistant.removePageContext"
      testId="ai-assistant-page-context"
      tone={tone}
    />
  );
}

export function DocumentContextChips({
  className,
  items,
  onRemove,
  tone = 'default',
}: DocumentContextChipsProps) {
  return (
    <ContextChips
      className={className}
      items={items.map((item) => ({
        key: item.id,
        label: item.label,
        title: item.id,
      }))}
      onRemove={onRemove}
      removeTestId="ai-assistant-document-context-remove"
      removeTitleKey="app.aiAssistant.removeDocumentContext"
      testId="ai-assistant-document-context"
      tone={tone}
    />
  );
}
